import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class ToastControllerService {
  constructor(private messageService: MessageService) {}

  successToastVisible(msg: unknown) {
    // Success toasts should never render error-flavoured text: fall back to a
    // neutral default when the caller passes an empty/undefined message.
    const detail = typeof msg === 'string' ? msg.trim() : this.formatErrorMessage(msg).trim();
    this.messageService.add({
      severity: 'success',
      summary: 'Done',
      detail: detail || 'Success',
      life: 3000,
    });
  }

  failureToastVisible(msg: unknown) {
    this.messageService.add({
      severity: 'error',
      summary: 'Something went wrong',
      detail: this.toDetail(msg),
      sticky: true,
    });
  }

  warningToastVisible(msg: unknown) {
    this.messageService.add({
      severity: 'warn',
      summary: 'Heads up',
      detail: this.toDetail(msg),
      sticky: true,
    });
  }

  /** Format an arbitrary payload for display, falling back to a friendly message when empty. */
  private toDetail(msg: unknown): string {
    return this.formatErrorMessage(msg).trim() || 'Something went wrong';
  }

  private formatErrorMessage(msg: unknown, seen: WeakSet<object> = new WeakSet()): string {
    if (msg === null || msg === undefined) {
      return '';
    }

    if (typeof msg === 'string') {
      return this.formatErrorString(msg, seen);
    }

    if (typeof msg === 'number' || typeof msg === 'boolean') {
      return String(msg);
    }

    // Network failures surface as a ProgressEvent/Event with no useful body.
    if (typeof Event !== 'undefined' && msg instanceof Event) {
      return 'Unable to reach the server';
    }

    if (Array.isArray(msg)) {
      return msg
        .map((item) => this.formatErrorMessage(item, seen))
        .filter((item) => item.length > 0)
        .join(', ');
    }

    if (typeof msg === 'object') {
      // Guard against circular references before recursing into children.
      if (seen.has(msg)) {
        return '';
      }
      seen.add(msg);

      const record = msg as Record<string, unknown>;
      const has = (key: string): boolean =>
        record[key] !== null && record[key] !== undefined;

      try {
        // Precedence matters: wrapped error, then a response envelope
        // ({ status, message, data }), then DRF detail, then a bare message.
        if (has('error')) return this.formatErrorMessage(record['error'], seen);
        if (has('message') && ('status' in record || 'data' in record)) {
          return this.formatErrorMessage(record['message'], seen);
        }
        if (has('detail')) return this.formatErrorMessage(record['detail'], seen);
        if (has('message')) return this.formatErrorMessage(record['message'], seen);

        // Key-value dictionary of field errors, e.g. { contact_number: ["This field may not be blank."] }
        const errorStrings: string[] = [];
        for (const [key, val] of Object.entries(record)) {
          if (val === null || val === undefined) continue;

          // Skip metadata fields if any slipped through
          if (
            ['status', 'data', 'code'].includes(key) &&
            (typeof val === 'number' || typeof val === 'string')
          ) {
            continue;
          }

          const formattedVal = this.formatErrorMessage(val, seen);
          if (!formattedVal) continue;

          if (['detail', 'message', 'error', 'non_field_errors', 'nonFieldErrors'].includes(key)) {
            errorStrings.push(formattedVal);
          } else {
            const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            errorStrings.push(`${fieldName}: ${formattedVal}`);
          }
        }

        if (errorStrings.length > 0) {
          return errorStrings.join('\n');
        }
      } catch (error) {
        // Exotic/non-enumerable object: log for developers rather than showing raw text.
        console.error('Failed to format error payload', error, msg);
      }

      // Unrecognised object: never dump raw JSON to the user (it may leak internal
      // detail); the caller substitutes a friendly default via toDetail().
      return '';
    }

    return '';
  }

  /** Handle a string leaf: unwrap stringified JSON envelopes, ignore raw HTML error pages. */
  private formatErrorString(msg: string, seen: WeakSet<object>): string {
    const trimmed = msg.trim();

    // A raw HTML error page (gateway or Django debug page) is not user-friendly.
    if (trimmed.startsWith('<')) {
      return '';
    }

    // A stringified JSON body that Angular did not parse should be unwrapped.
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed !== null && typeof parsed === 'object') {
          return this.formatErrorMessage(parsed, seen);
        }
      } catch {
        // Not valid JSON; fall through and return the raw string.
      }
    }

    return trimmed;
  }
}
